import { Film, Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { reportClientError } from "../../lib/client-error.js";
import { useMediaStore } from "../../stores/media-store.js";

function srcKind(src: string): string {
  if (src.startsWith("data:")) return "data";
  if (src.startsWith("blob:")) return "blob";
  if (src.startsWith("http")) return "http";
  return "other";
}

/**
 * Compact strip rendered above the editor when a media attachment is
 * present. Shows a thumbnail (photo) or filename + icon (audio), the
 * upload status, and a remove button. The full styled preview lives in
 * `TelegramPreview` (preview tab); this is the editor-side affordance.
 */
export function MediaPreviewSlot() {
  const { t } = useTranslation();
  const attachment = useMediaStore((s) => s.attachment);
  const clear = useMediaStore((s) => s.clear);

  if (!attachment) return null;

  const isPhoto = attachment.kind === "photo";
  const isVideo = attachment.kind === "video";
  const isUploading = attachment.status === "uploading";
  const isError = attachment.status === "error";
  // Prefer the persisted thumb dataURL — it renders reliably in
  // Telegram's Android WebView, where `blob:` URLs in <img> are
  // commonly broken or get OOM-skipped on multi-MB phone photos.
  const photoSrc = attachment.thumbDataUrl ?? attachment.blobUrl;
  const videoSrc = attachment.blobUrl;
  const hasPhotoSrc = photoSrc.length > 0;
  const hasVideoSrc = videoSrc.length > 0;

  return (
    <div className="flex items-center gap-2 border-b border-[var(--tg-secondary-bg)] bg-[var(--tg-secondary-bg)] px-3 py-2">
      {(isPhoto || isVideo) ? (
        <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-md bg-[var(--tg-bg)]">
          {isPhoto && hasPhotoSrc ? (
            <img
              src={photoSrc}
              alt=""
              className="h-full w-full object-cover"
              onError={() => {
                reportClientError({
                  source: "manual",
                  message: `media_slot_img_error src_kind=${srcKind(photoSrc)} len=${String(photoSrc.length)}`,
                  stack: JSON.stringify({
                    tag: "media_slot_img_error",
                    src_kind: srcKind(photoSrc),
                    src_length: photoSrc.length,
                    src_prefix: photoSrc.slice(0, 60),
                    has_thumb: !!attachment.thumbDataUrl,
                    has_original: !!attachment.originalDataUrl,
                    has_blob: attachment.blobUrl.length > 0,
                  }),
                });
              }}
            />
          ) : isVideo && hasVideoSrc ? (
            <video
              src={videoSrc}
              className="h-full w-full object-cover"
              muted
              playsInline
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[var(--tg-hint)]">
              <Film size={20} aria-hidden />
            </div>
          )}
          {isUploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <Loader2
                size={18}
                className="animate-spin text-white"
                aria-hidden
              />
            </div>
          )}
        </div>
      ) : (
        <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[var(--tg-bg)]">
          <span aria-hidden className="text-lg">
            🎵
          </span>
          {isUploading && (
            <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/40">
              <Loader2
                size={18}
                className="animate-spin text-white"
                aria-hidden
              />
            </div>
          )}
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-[var(--tg-text)]">
          {attachment.name}
        </div>
        <div
          className={`text-[10px] ${isError ? "text-red-500" : "text-[var(--tg-hint)]"}`}
        >
          {isError
            ? t("media.upload_failed")
            : isUploading && attachment.retryAttempt
              ? t("media.retrying", {
                  attempt: attachment.retryAttempt,
                  total: 3,
                })
              : isUploading
                ? t("media.uploading")
                : t(`media.kind.${attachment.kind}`)}
        </div>
      </div>

      <button
        type="button"
        aria-label={t("media.remove")}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--tg-hint)] hover:bg-[var(--tg-bg)]"
        onClick={() => {
          clear();
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}
